import { IconType } from "react-icons";
import {
  TbArrowsExchange,
  TbAward,
  TbBell,
  TbBook,
  TbBookmark,
  TbBrain,
  TbBriefcase,
  TbBug,
  TbBuilding,
  TbBuildingSkyscraper,
  TbBuildingStore,
  TbBulb,
  TbCalendar,
  TbCalendarCheck,
  TbCar,
  TbCertificate,
  TbChartLine,
  TbChartPie,
  TbCheckbox,
  TbChecklist,
  TbClock,
  TbCloud,
  TbCode,
  TbCoffee,
  TbCoin,
  TbCompass,
  TbConfetti,
  TbCrown,
  TbDatabase,
  TbDeviceDesktop,
  TbDeviceLaptop,
  TbDeviceMobile,
  TbEye,
  TbFileText,
  TbFlag,
  TbFlask,
  TbFolder,
  TbFriends,
  TbGift,
  TbHeadset,
  TbHeart,
  TbHeartHandshake,
  TbHome,
  TbHourglass,
  TbIdBadge2,
  TbKey,
  TbKeyboard,
  TbLeaf,
  TbLifebuoy,
  TbListCheck,
  TbLock,
  TbMail,
  TbMap,
  TbMapPin,
  TbMessage2,
  TbMoodSmile,
  TbPalette,
  TbPaperclip,
  TbPeace,
  TbPencil,
  TbPlane,
  TbPresentation,
  TbPuzzle,
  TbRefresh,
  TbRocket,
  TbRoute,
  TbScale,
  TbSchool,
  TbSeedling,
  TbServer,
  TbSettings,
  TbShieldCheck,
  TbShieldLock,
  TbShoppingCart,
  TbSparkles,
  TbSpeakerphone,
  TbStar,
  TbStethoscope,
  TbTarget,
  TbTool,
  TbTrophy,
  TbTruck,
  TbUserCheck,
  TbUserCog,
  TbUserPlus,
  TbUserStar,
  TbUsers,
  TbUsersGroup,
  TbVideo,
  TbWorld,
} from "react-icons/tb";

// ---------------------------------------------------------------------------
// 3 groups × 30 icons = 90. Each group renders as a 5-row × 6-col grid in the
// picker. Order matters — the first six icons in each list form row 1, etc.
// Keep in sync with Path::ICONS in app/models/path.rb.
// ---------------------------------------------------------------------------
export const PATH_ICON_GROUPS = [
  {
    key: "people",
    label: "People & Recognition",
    icons: [
      // Row 1 — primitives
      "folder", "users", "users-group", "friends", "user-check", "user-plus",
      // Row 2 — specific people
      "user-star", "user-cog", "heart-handshake", "id-badge-2", "briefcase", "crown",
      // Row 3 — roles
      "presentation", "headset", "school", "mood-smile", "peace", "heart",
      // Row 4 — recognition
      "star", "award", "trophy", "certificate", "confetti", "sparkles",
      // Row 5 — moments & comms
      "gift", "coffee", "mail", "message-2", "bell", "bookmark",
    ],
  },
  {
    key: "journey",
    label: "Journey & Place",
    icons: [
      // Row 1 — start / growth
      "rocket", "seedling", "leaf", "refresh", "arrows-exchange", "calendar",
      // Row 2 — time / progress
      "calendar-check", "clock", "hourglass", "checklist", "list-check", "checkbox",
      // Row 3 — direction & focus
      "eye", "target", "flag", "route", "compass", "world",
      // Row 4 — places
      "map", "map-pin", "home", "building", "building-skyscraper", "building-store",
      // Row 5 — motion & docs
      "plane", "car", "truck", "device-mobile", "paperclip", "file-text",
    ],
  },
  {
    key: "tools",
    label: "Tools & Functions",
    icons: [
      // Row 1 — engineering
      "code", "bulb", "database", "palette", "cloud", "server",
      // Row 2 — devices
      "bug", "device-laptop", "device-desktop", "keyboard", "chart-line", "chart-pie",
      // Row 3 — GTM
      "video", "shopping-cart", "speakerphone", "coin", "settings", "scale",
      // Row 4 — back office
      "stethoscope", "tool", "flask", "pencil", "book", "brain",
      // Row 5 — support & security
      "lifebuoy", "puzzle", "shield-check", "shield-lock", "lock", "key",
    ],
  },
] as const;

export type PathIconKey = string;
export type PathIconGroupKey = (typeof PATH_ICON_GROUPS)[number]["key"];

export const PATH_ICON_KEYS: readonly string[] = PATH_ICON_GROUPS.flatMap((g) => g.icons);

const ICON_COMPONENTS: Record<string, IconType> = {
  // People & Recognition
  folder: TbFolder,
  users: TbUsers,
  "users-group": TbUsersGroup,
  friends: TbFriends,
  "user-check": TbUserCheck,
  "user-plus": TbUserPlus,
  "user-star": TbUserStar,
  "user-cog": TbUserCog,
  "heart-handshake": TbHeartHandshake,
  "id-badge-2": TbIdBadge2,
  briefcase: TbBriefcase,
  crown: TbCrown,
  presentation: TbPresentation,
  headset: TbHeadset,
  school: TbSchool,
  "mood-smile": TbMoodSmile,
  peace: TbPeace,
  heart: TbHeart,
  star: TbStar,
  award: TbAward,
  trophy: TbTrophy,
  certificate: TbCertificate,
  confetti: TbConfetti,
  sparkles: TbSparkles,
  gift: TbGift,
  coffee: TbCoffee,
  mail: TbMail,
  "message-2": TbMessage2,
  bell: TbBell,
  bookmark: TbBookmark,
  // Journey & Place
  rocket: TbRocket,
  seedling: TbSeedling,
  leaf: TbLeaf,
  refresh: TbRefresh,
  "arrows-exchange": TbArrowsExchange,
  calendar: TbCalendar,
  "calendar-check": TbCalendarCheck,
  clock: TbClock,
  hourglass: TbHourglass,
  checklist: TbChecklist,
  "list-check": TbListCheck,
  checkbox: TbCheckbox,
  eye: TbEye,
  target: TbTarget,
  flag: TbFlag,
  route: TbRoute,
  compass: TbCompass,
  world: TbWorld,
  map: TbMap,
  "map-pin": TbMapPin,
  home: TbHome,
  building: TbBuilding,
  "building-skyscraper": TbBuildingSkyscraper,
  "building-store": TbBuildingStore,
  plane: TbPlane,
  car: TbCar,
  truck: TbTruck,
  "device-mobile": TbDeviceMobile,
  paperclip: TbPaperclip,
  "file-text": TbFileText,
  // Tools & Functions
  code: TbCode,
  bulb: TbBulb,
  database: TbDatabase,
  palette: TbPalette,
  cloud: TbCloud,
  server: TbServer,
  bug: TbBug,
  "device-laptop": TbDeviceLaptop,
  "device-desktop": TbDeviceDesktop,
  keyboard: TbKeyboard,
  "chart-line": TbChartLine,
  "chart-pie": TbChartPie,
  video: TbVideo,
  "shopping-cart": TbShoppingCart,
  speakerphone: TbSpeakerphone,
  coin: TbCoin,
  settings: TbSettings,
  scale: TbScale,
  stethoscope: TbStethoscope,
  tool: TbTool,
  flask: TbFlask,
  pencil: TbPencil,
  book: TbBook,
  brain: TbBrain,
  lifebuoy: TbLifebuoy,
  puzzle: TbPuzzle,
  "shield-check": TbShieldCheck,
  "shield-lock": TbShieldLock,
  lock: TbLock,
  key: TbKey,
};

// Aliases keep older path records rendering correctly after the icon set was revamped.
// Keys here are no longer shown in the picker, but still resolve to a sensible component.
const ICON_ALIASES: Record<string, string> = {
  // v1 aliases
  chart: "chart-line",
  globe: "world",
  headphones: "headset",
  clipboard: "checklist",
  shield: "shield-check",
  monitor: "device-desktop",
  zap: "rocket",
  smile: "mood-smile",
  package: "gift",
  // v2 → v3 reshuffles (none — kept entire v2 set in v3)
};

export const DEPRECATED_ICON_KEYS = Object.keys(ICON_ALIASES);

// Curated color palette. Keep in sync with Path::ICON_COLORS in app/models/path.rb.
export const PATH_ICON_COLOR_KEYS = [
  "gray",
  "blue",
  "indigo",
  "purple",
  "pink",
  "red",
  "orange",
  "yellow",
  "green",
  "teal",
] as const;

export type PathIconColorKey = (typeof PATH_ICON_COLOR_KEYS)[number];

interface IconColorPalette {
  bg: string;
  fg: string;
}

const COLOR_PALETTES: Record<PathIconColorKey, IconColorPalette> = {
  gray: { bg: "gray.100", fg: "gray.600" },
  blue: { bg: "blue.50", fg: "blue.600" },
  indigo: { bg: "cdsIndigo.50", fg: "cdsIndigo.600" },
  purple: { bg: "purple.50", fg: "purple.600" },
  pink: { bg: "pink.50", fg: "pink.600" },
  red: { bg: "red.50", fg: "red.600" },
  orange: { bg: "orange.50", fg: "orange.600" },
  yellow: { bg: "yellow.50", fg: "yellow.700" },
  green: { bg: "green.50", fg: "green.700" },
  teal: { bg: "teal.50", fg: "teal.600" },
};

export const DEFAULT_ICON: PathIconKey = "folder";
export const DEFAULT_ICON_COLOR: PathIconColorKey = "gray";

export function isPathIconKey(value: string | null | undefined): value is PathIconKey {
  if (!value) return false;
  return value in ICON_COMPONENTS || value in ICON_ALIASES;
}

export function isPathIconColorKey(value: string | null | undefined): value is PathIconColorKey {
  return !!value && (PATH_ICON_COLOR_KEYS as readonly string[]).includes(value);
}

export function iconComponentFor(key: string | null | undefined): IconType {
  if (!key) return ICON_COMPONENTS[DEFAULT_ICON];
  const resolved = ICON_ALIASES[key] ?? key;
  return ICON_COMPONENTS[resolved] ?? ICON_COMPONENTS[DEFAULT_ICON];
}

export function iconColorPaletteFor(key: string | null | undefined): IconColorPalette {
  return isPathIconColorKey(key) ? COLOR_PALETTES[key] : COLOR_PALETTES[DEFAULT_ICON_COLOR];
}

export function groupKeyForIcon(icon: string | null | undefined): PathIconGroupKey {
  if (!icon) return PATH_ICON_GROUPS[0].key;
  const resolved = ICON_ALIASES[icon] ?? icon;
  const group = PATH_ICON_GROUPS.find((g) => (g.icons as readonly string[]).includes(resolved));
  return group?.key ?? PATH_ICON_GROUPS[0].key;
}

// Keyword → suggestion mapping. Earlier entries win on ties.
const SUGGESTION_RULES: Array<{ keywords: string[]; icon: PathIconKey; color: PathIconColorKey }> = [
  { keywords: ["preboard", "kickoff", "launch", "onboard"], icon: "rocket", color: "orange" },
  { keywords: ["engineer", "developer", "devops", "platform"], icon: "code", color: "indigo" },
  { keywords: ["sales", "revenue", "biz dev", "growth"], icon: "chart-line", color: "green" },
  { keywords: ["customer success", "support", "csm"], icon: "headset", color: "teal" },
  { keywords: ["remote", "wfh", "work from home"], icon: "home", color: "gray" },
  { keywords: ["contractor", "vendor", "agency", "freelance"], icon: "briefcase", color: "orange" },
  { keywords: ["intern", "apprentice", "graduate"], icon: "school", color: "yellow" },
  { keywords: ["executive", "leadership", "manager", "ceo", "leader"], icon: "crown", color: "purple" },
  { keywords: ["it", "security", "compliance", "info sec"], icon: "shield-check", color: "blue" },
  { keywords: ["design", "creative", "brand"], icon: "palette", color: "pink" },
  { keywords: ["finance", "accounting", "payroll"], icon: "coin", color: "gray" },
  { keywords: ["marketing", "comms", "content"], icon: "speakerphone", color: "red" },
  { keywords: ["product", "pm "], icon: "bulb", color: "purple" },
  { keywords: ["people", "hr", "talent", "recruit"], icon: "users-group", color: "teal" },
  { keywords: ["fte", "full time", "full-time", "employee"], icon: "user-check", color: "blue" },
  { keywords: ["global", "international", "worldwide"], icon: "world", color: "gray" },
  { keywords: ["everyone", "all hands", "company-wide"], icon: "folder", color: "gray" },
  { keywords: ["legal"], icon: "scale", color: "gray" },
  { keywords: ["operations", "ops"], icon: "settings", color: "gray" },
  { keywords: ["data"], icon: "database", color: "indigo" },
  { keywords: ["mentor", "buddy"], icon: "heart-handshake", color: "pink" },
  { keywords: ["training", "course", "academy"], icon: "school", color: "yellow" },
  { keywords: ["welcome"], icon: "sparkles", color: "purple" },
];

export interface IconSuggestion {
  icon: PathIconKey;
  color: PathIconColorKey;
}

export function suggestIconForName(
  name: string | undefined,
  usedIcons?: Set<string>,
): IconSuggestion | null {
  if (!name) return null;
  const normalized = name.toLowerCase();
  for (const rule of SUGGESTION_RULES) {
    if (rule.keywords.some((keyword) => normalized.includes(keyword))) {
      if (!usedIcons?.has(rule.icon)) return { icon: rule.icon, color: rule.color };
    }
  }
  return null;
}
